// Number formatters. Mirrors TP.fmt* in the design reference (app.jsx).
// All money math at the UI layer is presentational; the source-of-truth
// math lives backend-side in Decimal per TDD §15.1. These helpers exist
// only to render numbers the trader can read at a glance.

export const fmtUSD = (n: number): string => {
  const sign = n < 0 ? "-" : "";
  const abs = Math.abs(n);
  return (
    sign +
    "$" +
    abs.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  );
};

export const fmtPx = (n: number): string =>
  "$" +
  n.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export const fmtPct = (n: number): string =>
  (n >= 0 ? "+" : "") + n.toFixed(2) + "%";

export const fmtSigned = (n: number): string =>
  (n >= 0 ? "+" : "-") +
  "$" +
  Math.abs(n).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

// Signed percent (e.g. "+1.2%" / "-1.2%"). Distinct from fmtSigned so we
// don't accidentally prefix "$" on a benchmark-comparison number.
export const fmtSignedPct = (n: number, dp = 1): string =>
  (n >= 0 ? "+" : "") + n.toFixed(dp) + "%";

// FX rate (Phase 9-UI). A rate like EUR/USD 1.0845 is NOT a dollar
// amount — no "$" prefix — and needs pair-specific precision
// (4 dp for most pairs, 2 dp for JPY-quoted; source of truth is
// fx_pairs.price_decimal_places). fmtPx's hard-pinned 2 dp would
// render EUR/USD as "1.08", destroying the tradeable range.
export const fmtRate = (n: number, decimalPlaces = 4): string =>
  n.toLocaleString("en-US", {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  });

// Asset-class-aware quantity formatting (Phase 9-UI). Consolidates
// the per-screen implementations (DayTradesScreen.formatQty was the
// only asset-aware one; CryptoScreen had its own private variant):
//   equity — whole shares;
//   crypto — fractional, up to 8 dp;
//   forex  — whole currency units, thousands-grouped (display
//            convention; 1000 units = one micro lot. The broker
//            API's own quantity unit — tastyfx uses lots — is the
//            Phase 9b adapter's concern, not this formatter's).
export const fmtQtyByClass = (
  n: number,
  assetClass: "equity" | "crypto" | "forex" | undefined,
): string => {
  if (assetClass === "crypto") {
    return n.toLocaleString("en-US", { maximumFractionDigits: 8 });
  }
  if (assetClass === "forex") {
    return Math.round(n).toLocaleString("en-US");
  }
  return String(Math.round(n));
};

// Unit label companion to fmtQtyByClass ("2,500 units" / "150 shares").
export const qtyUnitLabel = (
  assetClass: "equity" | "crypto" | "forex" | undefined,
): string => (assetClass === "crypto" || assetClass === "forex" ? "units" : "shares");

export const nowET = (): string => {
  // Display-only clock for the topbar. The backend clock facade
  // (tradingplatform.clock.now) is the only source of truth for actual
  // session/decision time. This value is for visual presence only.
  //
  // Rendered in actual US/Eastern time — the label says "ET", so using
  // the browser's local timezone would mislead any operator outside
  // Eastern.
  return new Date().toLocaleTimeString("en-US", {
    timeZone: "America/New_York",
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};
